import { isolateFormattedValue } from './date-time'
import { intlLocale, type Locale } from './locales'

const CALENDAR = 'islamic-umalqura'

/**
 * Intl falls back to the Gregorian calendar, without an error, when an engine
 * does not know the requested one. Month 9 would then be September, so every
 * reader asks the formatter which calendar it really used.
 */
function hijriFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat | null {
  const formatter = new Intl.DateTimeFormat(`${locale}-u-ca-${CALENDAR}`, options)
  return formatter.resolvedOptions().calendar === CALENDAR ? formatter : null
}

function withCalendar(locale: string): string {
  // `ar-u-nu-latn` already carries a Unicode extension; add the calendar to it.
  return locale.includes('-u-')
    ? locale.replace('-u-', `-u-ca-${CALENDAR}-`)
    : `${locale}-u-ca-${CALENDAR}`
}

/** The Umm al-Qura month (1-12) and day of `value` in `timeZone`, or null where the engine has no such calendar. */
export function hijriMonthDay(
  value: Date,
  timeZone: string,
): { month: number; day: number } | null {
  const formatter = hijriFormatter('en', { month: 'numeric', day: 'numeric', timeZone })
  if (!formatter) return null
  const parts = formatter.formatToParts(value)
  const read = (type: 'month' | 'day') => Number(parts.find((part) => part.type === type)?.value)
  return { month: read('month'), day: read('day') }
}

/**
 * The Umm al-Qura date in the house style: "6 Jumada I 1448 AH", and in Arabic
 * "6 جمادى الأولى 1448 هـ" with Latin digits like every other date. Empty where
 * the engine has no Umm al-Qura calendar.
 */
export function formatHijriDate(value: Date, locale: Locale, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  }
  const formatter = new Intl.DateTimeFormat(withCalendar(intlLocale(locale)), options)
  if (formatter.resolvedOptions().calendar !== CALENDAR) return ''
  if (locale !== 'en') return isolateFormattedValue(formatter.format(value), locale)
  const parts = formatter.formatToParts(value)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? ''
  return `${part('day')} ${part('month')} ${part('year')} ${part('era')}`.trim()
}
